import { z } from 'zod';
import { EVENT_TYPES, OPENING_EVENT_TYPES, type EventType } from '../enums';
import { clientCreatedAt, isoDate, optText, santim, uuid, type SyncFields } from './common';
import { eventDataSchemas, type EventDataMap } from './event-data';

export const baseEventSchema = z.object({
  animalId: uuid,
  type: z.enum(EVENT_TYPES),
  date: isoDate,
  data: z.record(z.string(), z.unknown()).default({}),
  costSantim: santim(0).nullish(),
  isOpening: z.boolean().default(false),
  note: optText(500),
  clientCreatedAt,
});

/**
 * Event (requirements §10.2, §10.3). `data` is validated per type with `eventDataSchemas[type]`,
 * plus the cross-field rules of sprint 3 §5. The output has `data` normalized (defaults applied).
 */
export const eventSchema = baseEventSchema
  .superRefine((e, ctx) => {
    const r = eventDataSchemas[e.type].safeParse(e.data);
    if (!r.success) {
      for (const issue of r.error.issues) {
        ctx.addIssue({ code: 'custom', message: issue.message, path: ['data', ...issue.path.map(String)] });
      }
      return;
    }
    const data = r.data as Record<string, unknown>;
    if (e.type === 'CALVING') {
      const calves = (data.calves ?? []) as Array<{ alive: boolean; animalId?: string }>;
      calves.forEach((c, i) => {
        if (c.alive && !c.animalId)
          ctx.addIssue({ code: 'custom', message: 'Live calves need an animal', path: ['data', 'calves', String(i), 'animalId'] });
      });
      if (e.isOpening && calves.length > 0)
        ctx.addIssue({ code: 'custom', message: 'Opening calvings have no calves', path: ['data', 'calves'] });
    }
    if (e.type === 'TREATMENT') {
      if (data.problem === 'OTHER' && !data.problemOther)
        ctx.addIssue({ code: 'custom', message: 'Describe the problem', path: ['data', 'problemOther'] });
      if (typeof data.lastDate === 'string' && data.lastDate < e.date)
        ctx.addIssue({ code: 'custom', message: 'Last treatment date is before the first', path: ['data', 'lastDate'] });
    }
    if (e.isOpening && !OPENING_EVENT_TYPES.includes(e.type))
      ctx.addIssue({ code: 'custom', message: 'Not an opening event type', path: ['isOpening'] });
  })
  .transform((e) => ({ ...e, data: eventDataSchemas[e.type].parse(e.data) as Record<string, unknown> }));

export type EventData = z.output<typeof eventSchema>;
export type EventRecord = EventData & SyncFields;

/** Typed view of an event's data. */
export function eventDataOf<T extends EventType>(e: { type: EventType; data: unknown }, type: T): EventDataMap[T] | null {
  return e.type === type ? (e.data as EventDataMap[T]) : null;
}
